import React, {useEffect, useRef, useState} from 'react';
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import Alert from "../../../../components/alert/alert.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import InputRadio from "../../../../components/input/input-radio.jsx";
import {FaArrowLeft, FaEnvelope} from "react-icons/fa";
import {FaCircleExclamation, FaCircleQuestion} from "react-icons/fa6";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import loginService from "../../../../services/web/login-service.js";
import ResetRetryCountdown from "../../../../components/reset-retry-countdown/reset-retry-countdown.js";
import HandleHashInput from "../../../../components/handle-hash/input.js";
import HandleHashPaste from "../../../../components/handle-hash/paste.js";
import {buildLoginData} from "../utils/login-utils.js";

const TwoFactorModal = ({open, setOpen, twoFactor, credentials, useCaptcha, onSuccess}) => {
    const {executeRecaptcha} = useGoogleReCaptcha();

    const [step, setStep] = useState("choose");
    const [method, setMethod] = useState(null);
    const [authTitle, setAuthTitle] = useState("");
    const [showResend, setShowResend] = useState(true);
    const [trustDevice, setTrustDevice] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const hashRef = useRef([]);
    const resendBtn = useRef();
    const resendCode = useRef();
    const timerEl = useRef();
    const timer = useRef();

    const resetState = () => {
        setStep("choose");
        setMethod(null);
        setAuthTitle("");
        setShowResend(true);
        setTrustDevice(false);
        setErrorMessage("");
        setLoading(false);
        hashRef.current.forEach(el => { if (el) { el.value = ""; el.classList.remove("border-red-500"); } });

        if (timer.current) {
            clearInterval(timer.current);
            timer.current = null;
        }
    };

    useEffect(() => {
        if (open) resetState();
    }, [open]);

    useEffect(() => {
        return () => {
            if (timer.current) clearInterval(timer.current);
        };
    }, []);

    const options = [
        {key: "TOTP", enabled: twoFactor?.totp, title: "Google Authenticator"},
        {key: "EMAIL", enabled: twoFactor?.email, title: "E-mail"},
        {key: "SMS", enabled: twoFactor?.phone, title: "SMS"},
    ].filter(option => option.enabled);

    const goToCodeStep = (title, withResend) => {
        setAuthTitle(title);
        setShowResend(withResend);
        setErrorMessage("");
        hashRef.current.forEach(el => { if (el) { el.value = ""; el.classList.remove("border-red-500"); } });
        setStep("code");
    };

    const startTwoFactor = (isResend = false) => {
        if (!method) {
            setErrorMessage("Selecione um método para prosseguir.");
            return;
        }

        if (method === "TOTP") {
            goToCodeStep("O app gera um código que deverá ser inserido no login", "Google Authenticator", false);
            return;
        }

        if (!isResend) setLoading(true);

        const data = {login: credentials.username, password: credentials.password, type: method};

        loginService.twoFactorStart(data)
            .then(({destination, remainingSeconds}) => {
                goToCodeStep(`Insira o código de 6 dígitos enviado para o seu ${destination}:`, true);
                ResetRetryCountdown(resendBtn, resendCode, timerEl, false, timer, remainingSeconds ?? 60, () => startTwoFactor(true));
            })
            .catch(error => {
                console.error(error);
                setErrorMessage("Não foi possível iniciar o processo.");
            })
            .finally(() => { if (!isResend) setLoading(false); });
    };

    const complete = async () => {
        const hash = hashRef.current.map(el => el?.value ?? "").join("");

        if (hash.length !== 6) {
            setErrorMessage("Preencha o código.");
            return;
        }

        setLoading(true);

        const extra = {two_factor_hash: hash, trust_device: trustDevice};

        if (useCaptcha) {
            if (!executeRecaptcha) {
                setErrorMessage("Não foi possível validar a segurança. Tente novamente em instantes.");
                setLoading(false);
                return;
            }

            const token = await executeRecaptcha("LOGIN").catch(() => null);

            if (!token) {
                setErrorMessage("Não foi possível validar a segurança. Tente novamente.");
                setLoading(false);
                return;
            }

            extra.token = token;
        }

        loginService.login(buildLoginData(credentials, extra))
            .then(result => {
                setOpen(false);
                onSuccess(result);
            })
            .catch(error => {
                console.error(error);
                const message = error.response?.data?.message ?? error.response?.data;

                if (message === "invalid.hash") {
                    setErrorMessage("Código inválido.");
                    hashRef.current.forEach(el => el?.classList.add("border-red-500"));
                    hashRef.current[0]?.focus();
                    hashRef.current[0]?.select();
                } else {
                    setErrorMessage("Não foi possível concluir a autenticação.");
                }
            })
            .finally(() => setLoading(false));
    };

    const cancel = () => {
        resetState();
        setOpen(false);
    };

    return (
        <Modal
            open={open}
            setOpen={() => {}}
            className="w-[580px] max-w-full"
            title={<img src={getAssetPath("img/logo.svg")} alt="Sentry" className="w-[100px]"/>}
            showCloseButton={false}
        >
            <Modal.Body>
                <div hidden={step !== "choose"}>
                    <div className="text-center mb-4">
                        <h4 className="font-semibold text-[18px] mb-4">Autenticação de dois fatores</h4>
                        <span className="text-[15px] mb-2">Escolha como prosseguir</span>
                    </div>

                    <div className="flex flex-col gap-2 items-center w-full">
                        {options.map(option => (
                            <div
                                key={option.key}
                                onClick={() => setMethod(option.key)}
                                className="flex items-center gap-3 rounded-lg px-4 py-6 cursor-pointer bg-[#E6E7EB] w-[290px]"
                            >
                                <InputRadio
                                    id={"two-factor-" + option.key.toLowerCase()}
                                    name="two-factor-method"
                                    checked={method === option.key}
                                    onChange={() => setMethod(option.key)}
                                />
                                <div className="flex flex-col text-start">
                                    <span className="font-semibold text-[15px]">{option.title}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div hidden={step !== "code"}>
                    <div className="relative mb-4">
                        <span className="absolute flex items-center gap-1 text-[14px] cursor-pointer" onClick={() => { setStep("choose"); setErrorMessage(""); }}>
                            <FaArrowLeft/> Voltar
                        </span>
                        <div className="text-center font-semibold text-[16px]">Autenticação de dois fatores</div>
                    </div>

                    <div className="text-center text-[15px] mb-4">
                        {authTitle}
                        <br/>
                    </div>

                    <div className="flex justify-center items-center mb-4">
                        <div className="flex flex-row gap-3">
                            {[...Array(6)].map((_, index) => (
                                <input
                                    key={index}
                                    className="bg-white bg-none border border-[#e5e6e7] text-inherit block px-3 py-1.5 uppercase w-[35px] h-[35px] rounded-md"
                                    type="text"
                                    maxLength="1"
                                    required
                                    autoFocus={index === 0}
                                    ref={(el) => hashRef.current[index] = el}
                                    onInput={(e) => HandleHashInput(e, index, hashRef)}
                                    onPaste={(e) => HandleHashPaste(e, hashRef)}
                                />
                            ))}
                        </div>
                    </div>

                    <span hidden={!showResend} className="flex gap-1 justify-center items-center text-[#063A6C] text-[14px]" ref={resendBtn}>
                        <FaEnvelope/>
                        Não recebeu o código?
                        <span className="cursor-pointer" ref={resendCode}>Reenviar</span>
                        <span ref={timerEl}/>
                    </span>

                    <div className="mt-4">
                        <InputCheckbox
                            id="trust_device"
                            checked={trustDevice}
                            onChange={(e) => setTrustDevice(e.target.checked)}
                            label={
                                <span className="flex items-center gap-1">
                                    Salvar como dispositivo confiável
                                    <FaCircleQuestion className="text-gray-400" title="Os dispositivos confiáveis não exigem autenticação de dois fatores."/>
                                </span>
                            }
                        />
                    </div>
                </div>

                {
                    errorMessage &&
                    <Alert target="danger" className="mt-4 flex items-center gap-2 text-[15px]!">
                        <FaCircleExclamation/> {errorMessage}
                    </Alert>
                }
            </Modal.Body>

            <Modal.Footer>
                <Button target="secondary" onClick={cancel}>Cancelar</Button>
                {
                    step === "choose"
                        ? <Button onClick={() => startTwoFactor(false)} loading={loading}>Avançar</Button>
                        : <Button onClick={complete} loading={loading} loadingText="Entrando...">Concluir</Button>
                }
            </Modal.Footer>
        </Modal>
    );
};

export default TwoFactorModal;
